/**
 * Onboarding UI for setting up Strava API credentials
 */

export class OnboardingUI {
  constructor(auth, api, onComplete) {
    this.auth = auth;
    this.api = api;
    this.onComplete = onComplete;
    this.currentStep = 1;
  }

  /**
   * Show onboarding screen
   */
  show() {
    // Create onboarding container
    const container = document.createElement('div');
    container.id = 'onboarding';
    container.innerHTML = this.getOnboardingHTML();
    document.body.appendChild(container);

    // Attach event listeners
    this.attachEventListeners();

    // Show first step
    this.showStep(1);
  }

  /**
   * Hide onboarding screen
   */
  hide() {
    const container = document.getElementById('onboarding');
    if (container) {
      container.remove();
    }
  }

  /**
   * Get the correct domain for OAuth callback
   * For manual OAuth flow, we use a dummy localhost URL
   */
  getCallbackDomain() {
    return 'localhost:9999';
  }

  /**
   * Get the correct website URL for OAuth
   * For manual OAuth flow, we use a dummy localhost URL
   */
  getWebsiteURL() {
    return 'http://localhost:9999';
  }

  /**
   * Get onboarding HTML
   */
  getOnboardingHTML() {
    const websiteURL = this.getWebsiteURL();
    const callbackDomain = this.getCallbackDomain();

    return `
      <div class="onboarding-overlay">
        <div class="onboarding-modal">
          <div class="onboarding-header">
            <h1><i class="fas fa-map-marked-alt"></i> Strava Activity Map</h1>
            <p>Visualize and export your Strava activities as animated GIFs</p>
          </div>

          <!-- Step 1: Welcome & Privacy -->
          <div class="onboarding-step" data-step="1">
            <h2>Welcome! <i class="fas fa-hand-wave"></i></h2>

            <div class="privacy-notice">
              <h3><i class="fas fa-lock"></i> Privacy First</h3>
              <ul>
                <li><i class="fas fa-check-circle" style="color: #0066cc;"></i> <strong>100% client-side</strong> - your data stays in your browser</li>
                <li><i class="fas fa-check-circle" style="color: #0066cc;"></i> <strong>Your own API credentials</strong> - use your Strava quota</li>
              </ul>
              <p><small>Not affiliated with Strava, Inc.</small></p>
            </div>

            <button class="btn-primary" onclick="onboarding.nextStep()">Get Started</button>
          </div>

          <!-- Step 2: Create Strava API App -->
          <div class="onboarding-step" data-step="2" style="display: none;">
            <h2>Step 1: Create API Application</h2>

            <div class="instructions">
              <ol>
                <li>Go to <a href="https://www.strava.com/settings/api" target="_blank" rel="noopener">Strava API Settings</a></li>
                <li>Fill in the form:
                  <ul>
                    <li><strong>Application Name:</strong> "My Activity Map"</li>
                    <li><strong>Category:</strong> "Visualizer"</li>
                    <li><strong>Website:</strong> <code id="website-url">${websiteURL}</code>
                      <button class="btn-copy" onclick="onboarding.copyToClipboard('website-url')">Copy</button>
                    </li>
                    <li><strong>Callback Domain:</strong> <code id="callback-domain">${callbackDomain}</code>
                      <button class="btn-copy" onclick="onboarding.copyToClipboard('callback-domain')">Copy</button>
                    </li>
                  </ul>
                </li>
                <li>Click "Create" and copy <strong>Client ID</strong> & <strong>Client Secret</strong></li>
              </ol>
            </div>

            <div class="button-row">
              <button class="btn-secondary" onclick="onboarding.prevStep()">Back</button>
              <button class="btn-primary" onclick="onboarding.nextStep()">Next</button>
            </div>
          </div>

          <!-- Step 3: Enter Credentials -->
          <div class="onboarding-step" data-step="3" style="display: none;">
            <h2>Step 2: Enter Credentials</h2>

            <div class="form-group">
              <label for="client-id">Client ID</label>
              <input type="text" id="client-id" placeholder="e.g., 123456" />
            </div>

            <div class="form-group">
              <label for="client-secret">Client Secret</label>
              <input type="password" id="client-secret" placeholder="Your client secret" />
            </div>

            <div class="form-group" style="margin-top: 20px; padding: 15px; background: #f8f9fa; border-radius: 6px;">
              <label style="display: flex; align-items: center; gap: 10px; cursor: pointer; font-weight: 500;">
                <input type="checkbox" id="include-private-activities-oauth" checked style="width: auto; cursor: pointer; margin: 0;">
                <span><i class="fas fa-lock"></i> Include private activities</span>
              </label>
            </div>

            <div id="credentials-error" class="error-message" style="display: none;"></div>

            <div class="button-row">
              <button class="btn-secondary" onclick="onboarding.prevStep()">Back</button>
              <button class="btn-primary" onclick="onboarding.saveCredentialsAndShowAuth()">Next</button>
            </div>
          </div>

          <!-- Step 4: Manual Authorization -->
          <div class="onboarding-step" data-step="4" style="display: none;">
            <h2>Step 3: Authorize with Strava</h2>

            <div id="auth-instructions" class="instructions">
              <!-- Will be populated dynamically based on checkbox selection -->
            </div>

            <button class="btn-primary" onclick="onboarding.openAuthWindow()" style="margin-bottom: 20px;">
              Open Strava Authorization
            </button>

            <div class="form-group">
              <label for="auth-code">Paste the URL from Error Page</label>
              <input type="text" id="auth-code" placeholder="http://localhost:9999/exchange_token?code=..." />
            </div>

            <div id="auth-error" class="error-message" style="display: none;"></div>
            <div id="auth-progress" style="display: none; text-align: center; margin: 15px 0;">
              <div class="spinner"></div>
              <p>Exchanging code for access token...</p>
            </div>

            <div class="button-row">
              <button class="btn-secondary" id="auth-back-btn" onclick="onboarding.prevStep()">Back</button>
              <button class="btn-primary" id="auth-continue-btn" onclick="onboarding.exchangeAuthCode()" disabled>Continue</button>
            </div>
          </div>

          <!-- Step 5: Fetch Activities -->
          <div class="onboarding-step" data-step="5" style="display: none;">
            <h2>Step 4: Load Activities</h2>
            <p id="athlete-welcome"></p>

            <div id="fetch-options">
              <!-- Populated dynamically in initializeStep5 -->
            </div>

            <div class="progress-container" id="fetch-progress" style="display: none;">
              <div class="spinner"></div>
              <p>Fetching: <strong id="fetch-count">0</strong> activities</p>
            </div>

            <div id="fetch-error" class="error-message" style="display: none;"></div>

            <div class="button-row" style="margin-top: 20px;">
              <button class="btn-secondary" onclick="onboarding.startOver()">Start Over</button>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Attach event listeners
   */
  attachEventListeners() {
    // Make onboarding methods available globally for onclick handlers
    window.onboarding = this;
  }

  /**
   * Show specific step
   */
  showStep(step) {
    // Hide all steps
    document.querySelectorAll('.onboarding-step').forEach(el => {
      el.style.display = 'none';
    });

    // Show current step
    const stepEl = document.querySelector(`[data-step="${step}"]`);
    if (stepEl) {
      stepEl.style.display = 'block';
      this.currentStep = step;
    }

    // Special handling for step 4 (authorization)
    if (step === 4) {
      this.initializeStep4();
    }

    // Special handling for step 5 (fetch activities)
    if (step === 5) {
      this.initializeStep5();
    }
  }

  /**
   * Next step
   */
  nextStep() {
    this.showStep(this.currentStep + 1);
  }

  /**
   * Previous step
   */
  prevStep() {
    this.showStep(this.currentStep - 1);
  }

  /**
   * Copy text to clipboard
   */
  copyToClipboard(elementId) {
    const el = document.getElementById(elementId);
    const text = el.textContent;

    navigator.clipboard.writeText(text).then(() => {
      // Show temporary success feedback
      const btn = event.target;
      const originalText = btn.textContent;
      btn.textContent = 'Copied!';
      setTimeout(() => {
        btn.textContent = originalText;
      }, 2000);
    });
  }

  /**
   * Save credentials and show auth step
   */
  saveCredentialsAndShowAuth() {
    const clientId = document.getElementById('client-id').value.trim();
    const clientSecret = document.getElementById('client-secret').value.trim();
    const includePrivate = document.getElementById('include-private-activities-oauth').checked;
    const errorEl = document.getElementById('credentials-error');

    // Validate
    if (!clientId || !clientSecret) {
      errorEl.textContent = 'Please enter both Client ID and Client Secret';
      errorEl.style.display = 'block';
      return;
    }

    // Validate Client ID is numeric
    if (!/^\d+$/.test(clientId)) {
      errorEl.textContent = 'Client ID should be a number (e.g., 123456)';
      errorEl.style.display = 'block';
      return;
    }

    // Save credentials with appropriate scope based on checkbox
    const scope = includePrivate ? 'read_all' : 'read';
    this.auth.saveCredentials(clientId, clientSecret, scope);

    // Populate auth instructions based on scope
    this.populateAuthInstructions(includePrivate);

    // Show manual auth step
    this.showStep(4);
  }

  /**
   * Populate authorization instructions based on private activities choice
   */
  populateAuthInstructions(includePrivate) {
    const instructionsEl = document.getElementById('auth-instructions');

    if (includePrivate) {
      instructionsEl.innerHTML = `
        <ol>
          <li>Click button below → <strong>check BOTH permissions</strong> → Authorize</li>
          <li>You'll see an error page (this is normal)</li>
          <li>Copy the URL from address bar and paste below</li>
        </ol>
      `;
    } else {
      instructionsEl.innerHTML = `
        <ol>
          <li>Click button below → <strong>check "View activities"</strong> → Authorize</li>
          <li>You'll see an error page (this is normal)</li>
          <li>Copy the URL from address bar and paste below</li>
        </ol>
      `;
    }
  }

  /**
   * Initialize step 4 (authorization)
   */
  initializeStep4() {
    const authCodeInput = document.getElementById('auth-code');
    const continueBtn = document.getElementById('auth-continue-btn');

    // Function to enable/disable continue button based on input
    const updateButtonState = () => {
      const hasText = authCodeInput.value.trim().length > 0;
      continueBtn.disabled = !hasText;
    };

    // Check initial state
    updateButtonState();

    // Add input listener
    authCodeInput.addEventListener('input', updateButtonState);

    // Also listen for paste event for immediate feedback
    authCodeInput.addEventListener('paste', () => {
      // Small delay to let paste complete
      setTimeout(updateButtonState, 10);
    });
  }

  /**
   * Open Strava authorization in new window
   */
  openAuthWindow() {
    try {
      const authUrl = this.auth.getAuthorizationUrl();
      window.open(authUrl, '_blank');
    } catch (error) {
      alert(`Error: ${error.message}`);
    }
  }

  /**
   * Exchange authorization code for token
   */
  async exchangeAuthCode() {
    const input = document.getElementById('auth-code').value.trim();
    const errorEl = document.getElementById('auth-error');
    const progressEl = document.getElementById('auth-progress');

    if (!input) {
      errorEl.textContent = 'Please paste the URL or authorization code';
      errorEl.style.display = 'block';
      return;
    }

    // Try to extract code and scope from URL if full URL was pasted
    let code = input;
    let grantedScope = 'read'; // Default to read-only

    // Check if input looks like a URL
    if (input.includes('code=')) {
      try {
        // Extract code parameter from URL
        const codeMatch = input.match(/code=([^&]+)/);
        if (codeMatch && codeMatch[1]) {
          code = codeMatch[1];
        } else {
          errorEl.textContent = 'Could not find authorization code in URL. Please paste the full URL from the error page.';
          errorEl.style.display = 'block';
          return;
        }

        // Extract scope parameter to determine what user granted
        const scopeMatch = input.match(/scope=([^&]+)/);
        if (scopeMatch && scopeMatch[1]) {
          const scopeString = decodeURIComponent(scopeMatch[1]);

          // Determine what scope user granted
          if (scopeString.includes('activity:read_all')) {
            grantedScope = 'read_all';
          } else if (scopeString.includes('activity:read')) {
            grantedScope = 'read';
          } else {
            // User didn't grant any activity access
            errorEl.innerHTML = `
              <strong>Missing Required Permission</strong><br><br>
              You need to grant access to view activities for this app to work.<br><br>
              Please click "Open Strava Authorization" again and make sure to check the <strong>"View data about your activities"</strong> permission box on Strava's page.
            `;
            errorEl.style.display = 'block';
            return;
          }
        }
      } catch (e) {
        errorEl.textContent = 'Could not parse URL. Please paste the full URL from the error page.';
        errorEl.style.display = 'block';
        return;
      }
    }

    try {
      errorEl.style.display = 'none';
      progressEl.style.display = 'block';

      // Save the actual granted scope (what user approved on Strava)
      const { clientId, clientSecret } = this.auth.getCredentials();
      this.auth.saveCredentials(clientId, clientSecret, grantedScope);

      // Exchange code for token
      await this.auth.exchangeCode(code);

      // Success! Move to fetch activities step
      progressEl.style.display = 'none';
      this.showStep(5);

    } catch (error) {
      console.error('Auth code exchange failed:', error);
      errorEl.textContent = `Failed to exchange code: ${error.message}`;
      errorEl.style.display = 'block';
      progressEl.style.display = 'none';
    }
  }

  /**
   * Format a date as DD MMM YYYY
   */
  _formatDate(date) {
    if (!date) return '?';
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
  }

  /**
   * Initialize step 5 (fetch activities)
   */
  async initializeStep5() {
    // Show athlete info
    const athlete = this.auth.getAthlete();
    const scope = sessionStorage.getItem(this.auth.storageKeys.scope);
    const hasPrivateAccess = scope === 'read_all';

    if (athlete) {
      const loadingMessage = hasPrivateAccess
        ? '<i class="fas fa-lock-open" style="color: #4caf50;"></i> Loading <strong>public and private activities</strong>'
        : '<i class="fas fa-lock" style="color: #666;"></i> Loading <strong>only public activities</strong>';

      document.getElementById('athlete-welcome').innerHTML =
        `Welcome, ${athlete.firstname} ${athlete.lastname}! <i class="fas fa-hand-wave" style="color: #fc4c02;"></i><br><small style="color: #666;">${loadingMessage}</small>`;
    }

    const optionsEl = document.getElementById('fetch-options');
    const cacheInfo = await this.api.getCacheInfo();

    if (cacheInfo) {
      const fromStr = this._formatDate(cacheInfo.minDate);
      const toStr = this._formatDate(cacheInfo.maxDate);
      const newFromStr = this._formatDate(cacheInfo.maxDate); // fetch starts from last day

      optionsEl.innerHTML = `
        <div class="fetch-option">
          <button class="btn-primary" onclick="onboarding.useCachedActivities()">
            Use cached activities
          </button>
          <small>${fromStr} – ${toStr} &nbsp;·&nbsp; ${cacheInfo.count} activities</small>
        </div>

        <div class="fetch-option">
          <button class="btn-primary" onclick="onboarding.fetchNewActivities()">
            Fetch new activities
          </button>
          <small>From ${newFromStr}</small>
        </div>

        <div class="fetch-option">
          <button class="btn-secondary" onclick="onboarding.fetchAllActivities()">
            Fetch all activities
          </button>
          <small>Deletes cache and re-fetches everything</small>
        </div>
      `;
    } else {
      optionsEl.innerHTML = `
        <div class="fetch-option">
          <button class="btn-primary" onclick="onboarding.fetchAllActivities()">
            Fetch Activities
          </button>
          <small>Download all your activities from Strava</small>
        </div>
      `;
    }
  }

  /**
   * Use cached activities
   */
  async useCachedActivities() {
    const activities = await this.api.getCachedActivities();
    if (activities) {
      this.complete(activities);
    }
  }

  /**
   * Fetch only new activities (since last cached date) and merge with cache
   */
  async fetchNewActivities() {
    const cacheInfo = await this.api.getCacheInfo();
    if (!cacheInfo || !cacheInfo.maxDate) return;

    // Start from beginning of the last cached day to catch same-day uploads
    const afterDate = new Date(cacheInfo.maxDate);
    afterDate.setHours(0, 0, 0, 0);
    const afterEpoch = Math.floor(afterDate.getTime() / 1000);

    await this._doFetch(async (onProgress) => {
      const newActivities = await this.api.fetchActivitiesAfter(afterEpoch, onProgress);
      return this.api.mergeAndCacheActivities(newActivities);
    });
  }

  /**
   * Fetch all activities (clears cache first)
   */
  async fetchAllActivities() {
    await this.api.clearCache();
    await this._doFetch(async (onProgress) => {
      const activities = await this.api.fetchAllActivities(onProgress);
      await this.api.cacheActivities(activities);
      return activities;
    });
  }

  /**
   * Shared fetch UI logic
   */
  async _doFetch(fetchFn) {
    const progressEl = document.getElementById('fetch-progress');
    const countEl = document.getElementById('fetch-count');
    const errorEl = document.getElementById('fetch-error');
    const optionsEl = document.getElementById('fetch-options');

    try {
      optionsEl.style.display = 'none';
      progressEl.style.display = 'block';
      errorEl.style.display = 'none';

      const activities = await fetchFn((count) => {
        countEl.textContent = count;
      });

      this.complete(activities);

    } catch (error) {
      console.error('Failed to fetch activities:', error);
      errorEl.textContent = `Error: ${error.message}`;
      errorEl.style.display = 'block';
      progressEl.style.display = 'none';
      optionsEl.style.display = 'block';
    }
  }

  /**
   * Start over
   */
  async startOver() {
    if (confirm('This will clear all your data and start over. Continue?')) {
      this.auth.clearAll();
      await this.api.clearCache();
      this.showStep(1);
    }
  }

  /**
   * Complete onboarding
   */
  complete(activities) {
    this.hide();
    if (this.onComplete) {
      this.onComplete(activities);
    }
  }

  /**
   * Get onboarding CSS
   */
  static getCSS() {
    return `
      .onboarding-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.5);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 10000;
        overflow-y: auto;
        padding: 20px;
      }

      .onboarding-modal {
        background: white;
        border-radius: 12px;
        max-width: 600px;
        width: 100%;
        max-height: 90vh;
        overflow-y: auto;
        box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
      }

      .onboarding-header {
        background: linear-gradient(135deg, #fc4c02 0%, #e44402 100%);
        color: white;
        padding: 30px;
        text-align: center;
        border-radius: 12px 12px 0 0;
      }

      .onboarding-header h1 {
        margin: 0 0 10px 0;
        font-size: 28px;
      }

      .onboarding-header p {
        margin: 0;
        opacity: 0.9;
      }

      .onboarding-step {
        padding: 30px;
      }

      .onboarding-step h2 {
        margin: 0 0 15px 0;
        font-size: 22px;
        color: #333;
      }

      .onboarding-step p {
        margin: 0 0 15px 0;
        color: #666;
        line-height: 1.6;
      }

      .privacy-notice {
        background: #f0f9ff;
        border: 2px solid #0066cc;
        border-radius: 8px;
        padding: 20px;
        margin: 20px 0;
      }

      .privacy-notice h3 {
        margin: 0 0 10px 0;
        color: #0066cc;
        font-size: 18px;
      }

      .privacy-notice ul {
        margin: 0;
        padding-left: 20px;
      }

      .privacy-notice li {
        margin: 8px 0;
        color: #333;
      }

      .disclaimer-notice {
        background: #fff3cd;
        border: 2px solid #ffc107;
        border-radius: 8px;
        padding: 15px;
        margin: 20px 0;
        text-align: center;
      }

      .disclaimer-notice p {
        margin: 0;
        color: #856404;
        line-height: 1.4;
      }

      .instructions {
        background: #f5f5f5;
        border-radius: 8px;
        padding: 20px;
        margin: 20px 0;
      }

      .instructions ol {
        margin: 0;
        padding-left: 20px;
      }

      .instructions li {
        margin: 12px 0;
        line-height: 1.6;
      }

      .instructions ul {
        margin: 8px 0;
        padding-left: 20px;
      }

      .instructions code {
        background: white;
        padding: 4px 8px;
        border-radius: 4px;
        font-family: monospace;
        font-size: 13px;
        border: 1px solid #ddd;
      }

      .btn-copy {
        background: #0066cc;
        color: white;
        border: none;
        padding: 4px 8px;
        border-radius: 4px;
        font-size: 11px;
        cursor: pointer;
        margin-left: 8px;
      }

      .btn-copy:hover {
        background: #0052a3;
      }

      .form-group {
        margin: 20px 0;
      }

      .form-group label {
        display: block;
        margin-bottom: 8px;
        font-weight: 600;
        color: #333;
      }

      .form-group input {
        width: 100%;
        padding: 10px;
        border: 2px solid #ddd;
        border-radius: 6px;
        font-size: 14px;
        box-sizing: border-box;
      }

      .form-group input:focus {
        outline: none;
        border-color: #fc4c02;
      }

      .form-group small {
        display: block;
        margin-top: 5px;
        color: #999;
        font-size: 12px;
      }

      .btn-primary, .btn-secondary {
        padding: 12px 24px;
        border: none;
        border-radius: 6px;
        font-size: 14px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s;
      }

      .btn-primary {
        background: #fc4c02;
        color: white;
        width: 100%;
      }

      .btn-primary:hover:not(:disabled) {
        background: #e44402;
      }

      .btn-primary:disabled {
        background: #ccc;
        cursor: not-allowed;
      }

      .btn-secondary {
        background: #666;
        color: white;
      }

      .btn-secondary:hover {
        background: #555;
      }

      .button-row {
        display: flex;
        gap: 10px;
        margin-top: 20px;
      }

      .button-row button {
        flex: 1;
      }

      .spinner {
        border: 3px solid #f3f3f3;
        border-top: 3px solid #fc4c02;
        border-radius: 50%;
        width: 40px;
        height: 40px;
        animation: spin 1s linear infinite;
        margin: 20px auto;
      }

      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }

      .error-message {
        background: #fee;
        border: 1px solid #fcc;
        color: #c33;
        padding: 12px;
        border-radius: 6px;
        margin: 15px 0;
      }

      .cache-info {
        background: #e8f5e9;
        border: 2px solid #4caf50;
        border-radius: 8px;
        padding: 15px;
        margin: 15px 0;
      }

      .cache-info p {
        margin: 5px 0;
        color: #2e7d32;
      }

      .progress-container {
        text-align: center;
        margin: 20px 0;
      }

      .progress-container p {
        margin-top: 10px;
      }

      .fetch-option {
        margin: 12px 0;
      }

      .fetch-option button {
        width: 100%;
        margin-bottom: 4px;
      }

      .fetch-option small {
        display: block;
        text-align: center;
        color: #888;
        font-size: 12px;
      }
    `;
  }
}
